import React from "react";
import { motion as Motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function DistribucionTicketsBar({
  icon = "fa-solid fa-ticket",
  title = "Total Ticket",
  date = "",
  total = 0,
  data = [],
}) {
  // Calculate total for percentage calculations
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  // Calculate percentages for each data item
  const dataWithPercentages = data.map((item) => ({
    ...item,
    percentage: totalValue > 0 ? (item.value / totalValue) * 100 : 0,
  }));

  return (
    <div className="text-white min-w-fit">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-center mb-4 sm:mb-0">
        <div className="flex items-center justify-between mb-4 w-full sm:mb-0 sm:w-fit">
          {/* Left side - Icon and Title */}
          <div className="flex items-start sm:items-center space-x-3">
            <div className="bg-white/10 rounded-lg p-3 flex items-center justify-center">
              <FontAwesomeIcon icon={icon} className="text-4xl text-white" />
            </div>
          </div>

          {/* Right side - Total */}
          <div className="text-right ml-4">
            <div>
              <h3 className="text-base text-white">{title}</h3>
              {date && <p className="text-sm text-gray-300">{date}</p>}
            </div>
            <Motion.div
              className="text-3xl font-bold text-white"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2,
              }}
            >
              {total.toLocaleString()}
            </Motion.div>
          </div>
        </div>

        <div className="w-full ml-4">
          {/* Data Items */}
          <div className="flex flex-col items-start justify-between sm:flex-row sm:items-center mb-4 space-x-6">
            {dataWithPercentages.map((item, index) => (
              <Motion.div
                key={index}
                className="flex items-center space-x-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.1 * index,
                  duration: 0.3,
                }}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm font-medium text-white">
                  {item.label}
                </span>
                <Motion.span
                  className="text-sm font-bold text-white"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 + 0.1 * index }}
                >
                  {item.value.toLocaleString()}
                </Motion.span>
              </Motion.div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div className="flex h-full">
                {dataWithPercentages.map((item, index) => (
                  <Motion.div
                    key={index}
                    className="h-full"
                    style={{
                      backgroundColor: item.color,
                      width: `${item.percentage}%`,
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${item.percentage}%` }}
                    transition={{
                      duration: 1.2,
                      delay: 0.5 + 0.1 * index,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Percentage Labels */}
            <div className="flex mt-2 text-xs text-gray-300">
              {dataWithPercentages.map((item, index) => (
                <Motion.div
                  key={index}
                  style={{ width: `${item.percentage}%` }}
                  className="text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 + 0.1 * index }}
                >
                  {item.percentage > 10 && `${item.percentage.toFixed(1)}%`}
                </Motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      {totalValue !== total && (
        <div className="mt-3 text-xs text-gray-400 text-center">
          Distribución de {totalValue.toLocaleString()} elementos
        </div>
      )}
    </div>
  );
}
